import { fetchPushKey, subscribePush, unsubscribePush } from "../api/gigboard";
import { isStandalone } from "./installPrompt";
import { readPref, writePref } from "./prefs";

/*
 * Monday and Friday reminders (Web Push). The server decides when to send; this only turns them on or off for this
 * device. A device is identified by its push subscription, so a participant with a phone and a laptop opts in twice.
 */

export type PushState = "off" | "ios" | "denied" | "ask" | "on";

const SNOOZE = "push.later";
const SNOOZE_DAYS = 14;

export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** iPhone and iPad (iPadOS reports itself as a Mac with touch). */
export function isIos(userAgent = navigator.userAgent): boolean {
  return (
    /iphone|ipad|ipod/i.test(userAgent) ||
    (/macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)
  );
}

/** "Not now" on the hub hides the card for 14 days. The Gig Board always shows it. */
export function pushSnoozed(now = Date.now()): boolean {
  return now < Number(readPref(SNOOZE) || 0);
}

export function snoozePush(now = Date.now()): void {
  writePref(SNOOZE, String(now + SNOOZE_DAYS * 864e5));
}

/** VAPID public keys are base64url; PushManager wants the raw bytes. */
export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const base64 = (value + "=".repeat((4 - (value.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

let cachedKey: Promise<{ key: string; enabled: boolean }> | null = null;

function pushKey() {
  cachedKey ??= fetchPushKey().catch(() => {
    cachedKey = null;
    return { key: "", enabled: false };
  });
  return cachedKey;
}

/** Tests only. */
export function resetPushKeyCache(): void {
  cachedKey = null;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) await navigator.serviceWorker.register("/sw.js");
  return navigator.serviceWorker.ready;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

export async function pushState(): Promise<PushState> {
  const supported = pushSupported();
  const iosNeedsInstall = !supported && isIos() && !isStandalone();
  // Nothing this browser can do: no need to ask the server.
  if (!supported && !iosNeedsInstall) return "off";
  if (!(await pushKey()).enabled) return "off";
  if (iosNeedsInstall) return "ios";
  if (Notification.permission === "denied") return "denied";
  const sub = await currentSubscription();
  return sub && Notification.permission === "granted" ? "on" : "ask";
}

/** Asks for permission, subscribes this device and tells the server. Resolves false when permission is refused. */
export async function enablePush(): Promise<boolean> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;
  const reg = await registration();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array((await pushKey()).key),
    }));
  const keys = sub.toJSON().keys ?? {};
  if (!keys.p256dh || !keys.auth) throw new Error("This browser didn't give us a push key.");
  await subscribePush({ endpoint: sub.endpoint, p256dh: keys.p256dh, auth: keys.auth });
  return true;
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await unsubscribePush(sub.endpoint);
  await sub.unsubscribe();
}

/*
 * A device follows whoever signed in last: on each participant sign-in, an existing subscription is re-sent so the
 * server links it to this person (v2's pushResync). Quiet on any failure; the card still offers to turn it on.
 */
export async function resyncPush(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== "granted") return;
    const sub = await currentSubscription();
    const keys = sub?.toJSON().keys;
    if (sub && keys?.p256dh && keys.auth)
      await subscribePush({ endpoint: sub.endpoint, p256dh: keys.p256dh, auth: keys.auth });
  } catch {
    /* reminders are a nicety; never block the app */
  }
}
